/**
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps a mirrored
 * copy in `api/src/common/utils/resume-truncation.ts` — the same arrangement as
 * `api/src/common/constants/transcription.ts`.
 *
 * Keep both files in sync. A mismatch would cut the persisted message and the
 * text on screen at two different points, and the resumed answer would either
 * repeat a fragment or lose one. The rule is unit-tested API-side in
 * `api/src/conversation/resume-truncation.spec.ts`.
 */

/** Sentence-final punctuation, including the French ellipsis. */
const SENTENCE_END_CHARS = ".!?…";

/** The last character written is sentence-final punctuation. */
const ENDS_ON_SENTENCE_END = new RegExp(`[${SENTENCE_END_CHARS}]$`);

/**
 * A finished sentence: punctuation followed by whitespace. The whitespace is
 * required because a trailing `.` with nothing after it may be an abbreviation
 * the model was still typing, not the end of a sentence.
 */
const SENTENCE_END_BOUNDARY = new RegExp(
  `[${SENTENCE_END_CHARS}](?=\\s)`,
  "g",
);

/**
 * Cut an interrupted assistant answer back to its last clean boundary.
 *
 * This does not decide whether the model was done answering: the caller already
 * knows that, from the message being marked interrupted when the server cut the
 * stream. All this picks is *where* to cut the text that survived.
 *
 * A turn that dies mid-generation stops on an arbitrary character, often
 * mid-word (`Si on trouve `, `sur une situa`). Asking the model to continue
 * from exactly there makes it guess the characters it can no longer see, and
 * the two halves come back as broken prose (`"Si on trouve` + `solution qui
 * résout"`). Dropping the trailing fragment instead lets the model rewrite that
 * sentence — or that whole markdown line — from its start, at the cost of a few
 * regenerated words.
 *
 * Boundaries, whichever keeps the most text: the end of the last complete line,
 * or the end of the last complete sentence. Text that already ends on one is
 * returned untouched, and so is text with no boundary at all — a single
 * unpunctuated line is the entire answer, and dropping it would leave nothing
 * to continue from.
 */
export const truncateToLastCleanBoundary = (text: string): string => {
  const body = text.replace(/\s+$/, "");
  if (body.length === 0) {
    return "";
  }

  // The text already stops on sentence punctuation or on a line break, so
  // nothing was left half-written: there is no fragment to drop.
  if (ENDS_ON_SENTENCE_END.test(body) || /\n\s*$/.test(text)) {
    return text;
  }

  const lastLineEnd = body.lastIndexOf("\n") + 1;
  const sentenceEnds = [...body.matchAll(SENTENCE_END_BOUNDARY)];
  const lastSentenceEnd = sentenceEnds.length
    ? (sentenceEnds[sentenceEnds.length - 1].index ?? -1) + 1
    : 0;

  const boundary = Math.max(lastLineEnd, lastSentenceEnd);
  if (boundary <= 0) {
    return text;
  }

  return body.slice(0, boundary);
};
